import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Material-UI core
import { Description } from "@mui/icons-material";
import { Typography, Box, Grid } from "@mui/material";

// Hooks personalizados
import { useSearch } from "hooks/useSearch";
import { useValidateReload } from "hooks/useValidateReload";

// Componentes personalizados
import RegularButton from "components/CustomButtons/Button";
import CardTable from "components/CardTable/CardTable";

// Acciones de Redux
import {
  setNotesMigrationFilters,
  getNotesMigration,
  doNotesMigration,
  removeNotesSelection,
} from "redux/actions/notesMigration/notesMigrationActions";

// Otros
import { columns } from "./columns";
import { TableInputs } from "components/CardTable/TableInputs";
import { newDate } from "helpers/dateTools";
import { getDecimalFormat } from "helpers/getNumberFormats";
import { dateValidator } from "helpers/dateValidator";
import { useToggle } from "hooks/useToggle";
import { ModalV2 } from "components/Modal/ModalV2";

export const NotesMigration = () => {
  const tableKey = "notesMigration";
  const loadings = ["loadingNotesMigration", "loadingNotesMigrationActions"];
  const { setSnackbar } = useSearch();
  const {
    station,
    notesMigrationArr,
    startDate,
    endDate,
    hasChecked,
    counter,
    counterAmount,
  } = useSelector(({ notesMigration }) => notesMigration);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const dispatch = useDispatch();
  useValidateReload(loadings);
  const [open, setOpen] = useToggle();
  const [isOver, setIsOver] = useState(false);
  const limit = parseInt(process.env.REACT_APP_LIMIT_FETCH_NOTES) || 50;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const isValidDate = dateValidator(startDate, endDate, 7, setSnackbar);
      if (isValidDate) return;
      if (station.length === 0)
        return setSnackbar("No se ha seleccionado alguna planta.", "warning");
      const ids = station.map((sta) => sta.id);
      dispatch(getNotesMigration(startDate, endDate, ids));
    },
    [startDate, endDate, station]
  );

  const handleMigrate = useCallback(() => {
    const ids = notesMigrationArr
      .filter((x) => x.checked)
      .map((x) => ({ id: x.id, is5Millar: x.isFivePerThousand || false }));

    dispatch(doNotesMigration(ids, setOpen));
  }, [notesMigrationArr, setOpen]);

  const handleRemoveSelection = useCallback(() => {
    dispatch(removeNotesSelection());
  }, []);

  useEffect(() => {
    const checkedNotes = notesMigrationArr.filter((x) => x.checked).length;
    checkedNotes > limit ? setIsOver(true) : setIsOver(false);
  }, [notesMigrationArr, limit]);

  return (
    <>
      <CardTable
        icon={<Description />}
        title="Notas a Migrar"
        hasHeader
        data={notesMigrationArr}
        columns={columns}
        isLoading={isLoadingComp?.loadingNotesMigration}
        blockBtn
        tableKey={tableKey}
        hasControl
        controls={
          <>
            {hasChecked > 0 && (
              <>
                <RegularButton
                  color="info"
                  size="sm"
                  onClick={handleRemoveSelection}
                  disabled={
                    notesMigrationArr.size === 0 ||
                    isLoadingComp?.loadingNotesMigration ||
                    isLoadingComp?.loadingNotesMigrationActions
                  }
                >
                  remover selección
                </RegularButton>
                <RegularButton
                  color={isOver ? "error" : "info"}
                  size="sm"
                  onClick={setOpen}
                  disabled={
                    hasChecked === 0 ||
                    isOver ||
                    isLoadingComp?.loadingNotesMigration ||
                    isLoadingComp?.loadingNotesMigrationActions
                  }
                >
                  {isOver ? `No se pueden migrar mas de ${limit} notas` : "migrar"}
                </RegularButton>
              </>
            )}
          </>
        }
        tableContent={
          <>
            <TableInputs
              reducer="notesMigration"
              dates
              stations
              multiStations
              stationsGridWidth={6}
              dispatchAction={setNotesMigrationFilters}
              loading={isLoadingComp?.loadingNotesMigration}
              submitAction={handleSubmit}
              maxDate={newDate()}
            />
            <Grid container>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle1"
                  color="info"
                  style={{
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "flex-start",
                    marginRight: 20,
                    color: "#696969",
                    textTransform: "none",
                  }}
                >
                  La fecha utilizada para la migración de notas corresponde a la
                  columna Fecha de Liquidación
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body1"
                  color="info"
                  style={{
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "flex-end",
                    marginRight: 20,
                    textTransform: "none",
                  }}
                >
                  {counter > 0 &&
                    `Cantidad total: ${getDecimalFormat(counter)}`}
                  <br />
                  {counterAmount > 0 &&
                    `Monto Seleccionado: $${getDecimalFormat(counterAmount)}`}
                </Typography>
              </Grid>
            </Grid>
          </>
        }
      />
      <ModalV2
        open={open}
        onClose={setOpen}
        title={
          <Box style={{ display: "flex", justifyContent: "center" }}>
            ¿Seguro que desea migrar las notas seleccionadas?
          </Box>
        }
        justConfirm
        onSubmit={handleMigrate}
        isLoading={isLoadingComp?.loadingNotesMigrationActions}
      >
        <Typography
          variant="subtitle1"
          color="info"
          style={{
            marginTop: 10,
            display: "flex",
            justifyContent: "center",
            marginRight: 20,
            color: "#696969",
            textTransform: "none",
          }}
        >
          La fecha utilizada para la migración de notas corresponde a la columna
          Fecha de Liquidación
        </Typography>
      </ModalV2>
    </>
  );
};
